import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AiInsights } from '../types/moodboard';
import { ScrollArea } from './ui/scroll-area';
import { X, Send, ArrowLeft, Sparkles, TrendingUp } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell, BarChart, Bar } from 'recharts';
interface AiInsightsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insights: AiInsights;
  onHighlight?: (type: 'color' | 'tag', value: string | null) => void;
  activeHighlight?: {
    type: string;
    value: string;
  } | null;
}
const CustomTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-black text-white px-3 py-2 text-[10px] font-heading tracking-widest uppercase" data-id="element-72">
        {payload[0].payload.label}
      </div>;
  }
  return null;
};
function AnimatedCounter({
  value,
  duration = 1500
}: {
  value: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number | null = null;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.round(easeOutQuart * value));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  return <span data-id="element-73">{count}%</span>;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const mockSalesData = [{
  name: 'Capispalla',
  produzione: 40,
  vendite: 65
}, {
  name: 'Maglieria',
  produzione: 30,
  vendite: 45
}, {
  name: 'Pantaloni',
  produzione: 50,
  vendite: 40
}, {
  name: 'Accessori',
  produzione: 20,
  vendite: 80
}];
const mockColorSalesData = [{
  name: 'Nero',
  moodboard: 80,
  vendite: 90
}, {
  name: 'Cammello',
  moodboard: 60,
  vendite: 45
}, {
  name: 'Grigio',
  moodboard: 40,
  vendite: 70
}, {
  name: 'Navy',
  moodboard: 20,
  vendite: 85
}];
const mockFabricSalesData = [{
  name: 'Lana',
  moodboard: 75,
  vendite: 60
}, {
  name: 'Cotone',
  moodboard: 40,
  vendite: 85
}, {
  name: 'Lino',
  moodboard: 65,
  vendite: 30
}, {
  name: 'Cashmere',
  moodboard: 50,
  vendite: 40
}];
const getMaterialImage = (material: string) => {
  const m = material.toLowerCase();
  if (m.includes('lana') || m.includes('tweed')) return 'https://cdn.magicpatterns.com/uploads/5cMB3g4y2xs7FWUPr37vh2/71BpVrt8y0L._AC_SX466_.jpg';
  if (m.includes('cotone') || m.includes('denim')) return 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&w=300&q=80';
  if (m.includes('seta') || m.includes('velluto')) return 'https://images.unsplash.com/photo-1584308985160-b8504068307b?auto=format&fit=crop&w=300&q=80';
  if (m.includes('lino')) return 'https://cdn.magicpatterns.com/uploads/q5LyP2t2rpiHMqqbr6ZQTX/abbigliamento-misto-lino-beige.webp';
  if (m.includes('pelle')) return 'https://images.unsplash.com/photo-1551028719-00167b16eac5?auto=format&fit=crop&w=300&q=80';
  return 'https://images.unsplash.com/photo-1605518216938-7c31b7b14ad0?auto=format&fit=crop&w=300&q=80'; // fallback texture
};
export function AiInsightsPanel({
  open,
  onOpenChange,
  insights,
  onHighlight,
  activeHighlight
}: AiInsightsPanelProps) {
  const [isChatting, setIsChatting] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<{
    role: 'user' | 'ai';
    content: string;
  }[]>([]);
  // Reset chat state when panel closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setIsChatting(false);
        setMessages([]);
        setChatInput('');
      }, 300);
    }
  }, [open]);
  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    setIsChatting(true);
    setMessages(prev => [...prev, {
      role: 'user',
      content: chatInput
    }]);
    setChatInput('');
    // Mock AI response
    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'ai',
        content: 'Sto analizzando la tua richiesta rispetto alla direzione creativa attuale. I pattern visivi suggeriscono una forte propensione verso il minimalismo sartoriale. Vuoi che approfondisca un aspetto specifico?'
      }]);
    }, 1000);
  };
  return <AnimatePresence data-id="element-74">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.4
    }} className="fixed inset-0 z-50 bg-white flex flex-col" data-id="element-75">
          {/* Header */}
          <div className="px-6 sm:px-8 py-6 sm:py-8 border-b border-border flex items-center justify-between bg-white z-10" data-id="element-76">
            <div className="flex items-center gap-4" data-id="element-77">
              {isChatting && <button onClick={() => setIsChatting(false)} className="flex items-center gap-2 text-[#666] hover:text-black transition-colors" data-id="element-78">
                  <ArrowLeft className="w-4 h-4" data-id="element-79" />
                  <span className="font-heading text-[10px] tracking-[0.2em] uppercase hidden sm:inline" data-id="element-80">
                    Torna agli Insights
                  </span>
                </button>}
              {!isChatting && <h2 className="font-heading font-light text-[14px] tracking-[0.3em] uppercase flex items-center gap-3" data-id="element-81">
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} data-id="element-82" />
                  ANALISI IA
                </h2>}
            </div>
            <button onClick={() => onOpenChange(false)} className="text-[#666] hover:text-black transition-colors" aria-label="Chiudi Insights" data-id="element-83">
              <X className="w-6 h-6" strokeWidth={1.5} data-id="element-84" />
            </button>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 relative overflow-hidden bg-[#FAFAFA]" data-id="element-85">
            <AnimatePresence mode="wait" data-id="element-86">
              {!isChatting ? <motion.div key="widgets" variants={containerVariants} initial="hidden" animate="show" exit="exit" className="absolute inset-0 overflow-y-auto pb-32" data-id="element-87">
                  <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-id="element-88">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-89">
                      {/* Left Column - Main Insights */}
                      <div className="lg:col-span-7 space-y-12" data-id="element-90">
                        <motion.section variants={itemVariants} className="space-y-6 border-l-2 border-black pl-6" data-id="element-91">
                          <h4 className="font-heading font-bold text-[12px] tracking-[0.25em] uppercase text-black" data-id="element-92">
                            VISIONE D'INSIEME
                          </h4>
                          <p className="text-[18px] sm:text-[20px] font-sans leading-[1.6] text-[#1A1A1A] font-light" data-id="element-93">
                            {insights.summary}
                          </p>
                        </motion.section>

                        <motion.div variants={itemVariants} className="border-t border-border" data-id="element-94" />

                        {insights.directionMap && insights.directionMap.length > 0 && <motion.section variants={itemVariants} className="space-y-8" data-id="element-95">
                              <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-96">
                                MAPPA DIREZIONALE
                              </h4>
                              <div className="h-[400px] w-full relative bg-white border border-border p-4 rounded-xl" data-id="element-97">
                                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] font-heading tracking-[0.2em] text-[#999] uppercase" data-id="element-98">
                                  Espressivo
                                </div>
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] font-heading tracking-[0.2em] text-[#999] uppercase" data-id="element-99">
                                  Minimale
                                </div>
                                <div className="absolute top-1/2 left-4 -translate-y-1/2 -rotate-90 text-[10px] font-heading tracking-[0.2em] text-[#999] uppercase origin-left" data-id="element-100">
                                  Tradizione
                                </div>
                                <div className="absolute top-1/2 right-4 -translate-y-1/2 rotate-90 text-[10px] font-heading tracking-[0.2em] text-[#999] uppercase origin-right" data-id="element-101">
                                  Innovazione
                                </div>

                                <ResponsiveContainer width="100%" height="100%" data-id="element-102">
                                  <ScatterChart margin={{
                          top: 40,
                          right: 40,
                          bottom: 40,
                          left: 40
                        }} data-id="element-103">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={true} horizontal={true} data-id="element-104" />
                                    <XAxis type="number" dataKey="x" domain={[0, 100]} hide data-id="element-105" />
                                    <YAxis type="number" dataKey="y" domain={[0, 100]} hide data-id="element-106" />
                                    <Tooltip content={<CustomTooltip data-id="element-108" />} cursor={{
                            strokeDasharray: '3 3'
                          }} data-id="element-107" />
                                    <Scatter name="Direction" data={insights.directionMap} fill="#000" data-id="element-109">
                                      {insights.directionMap.map((entry, index) => <Cell key={`cell-${index}`} fill="#000" data-id="element-110" />)}
                                    </Scatter>
                                  </ScatterChart>
                                </ResponsiveContainer>
                              </div>
                            </motion.section>}

                        <motion.div variants={itemVariants} className="border-t border-border" data-id="element-111" />

                        {/* Sales Correlation Section */}
                      </div>

                      {/* Right Column - Details */}
                      <div className="lg:col-span-5 space-y-12" data-id="element-112">
                        <motion.section variants={itemVariants} className="space-y-4 bg-white p-8 border border-border rounded-xl" data-id="element-113">
                          <div className="flex items-end justify-between mb-4" data-id="element-114">
                            <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-115">
                              COERENZA VISIVA
                            </h4>
                            <span className="font-heading font-bold text-[48px] leading-none tracking-tight" data-id="element-116">
                              <AnimatedCounter value={insights.cohesionScore} data-id="element-117" />
                            </span>
                          </div>
                          <div className="w-full h-[2px] bg-[#E5E5E5]" data-id="element-118">
                            <div className="h-full bg-black transition-all duration-1500 ease-out" style={{
                        width: `${insights.cohesionScore}%`
                      }} data-id="element-119" />
                          </div>
                          <p className="text-[13px] text-[#666] font-sans mt-4" data-id="element-120">
                            {insights.cohesionScore > 70 ? 'Gli elementi mostrano una forte coerenza visiva e tematica.' : 'Elementi eterogenei. Valuta di restringere il focus creativo.'}
                          </p>
                        </motion.section>

                        <motion.section variants={itemVariants} className="space-y-6" data-id="element-121">
                          <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-122">
                            PALETTE DOMINANTE
                          </h4>
                          {insights.dominantColors.length > 0 ? <div className="flex flex-wrap gap-4" data-id="element-123">
                              {insights.dominantColors.map((color, idx) => {
                        const isActive = activeHighlight?.type === 'color' && activeHighlight.value === color;
                        return <button key={idx} className={`flex flex-col items-center gap-3 transition-opacity hover:opacity-80 ${isActive ? 'ring-2 ring-black ring-offset-2 rounded-full' : ''}`} onClick={() => onHighlight?.('color', color)} data-id="element-124">
                                    <div className="w-14 h-14 border border-border rounded-full" style={{
                            backgroundColor: color
                          }} data-id="element-125" />
                                    <span className="font-mono text-[10px] text-[#666] uppercase" data-id="element-126">
                                      {color}
                                    </span>
                                  </button>;
                      })}
                            </div> : <p className="text-[13px] text-[#666] font-sans" data-id="element-127">
                              Aggiungi una palette colori per iniziare.
                            </p>}
                        </motion.section>

                        <motion.section variants={itemVariants} className="space-y-6" data-id="element-128">
                          <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-129">
                            TEMI PRINCIPALI
                          </h4>
                          <div className="flex flex-col gap-3" data-id="element-130">
                            {insights.themes.length > 0 ? insights.themes.map((theme, idx) => {
                        const isActive = activeHighlight?.type === 'tag' && activeHighlight.value === theme;
                        const count = Math.max(1, 5 - idx);
                        return <button key={idx} onClick={() => onHighlight?.('tag', theme)} className={`group flex items-center justify-between p-4 border transition-all rounded-full ${isActive ? 'border-black bg-black text-white' : 'border-border bg-white hover:border-black'}`} data-id="element-131">
                                    <span className="font-heading text-[12px] tracking-[0.15em] uppercase px-2" data-id="element-132">
                                      {theme}
                                    </span>
                                    <span className={`font-mono text-[11px] ${isActive ? 'text-white/70' : 'text-[#999] group-hover:text-black'}`} data-id="element-133">
                                      {count}
                                    </span>
                                  </button>;
                      }) : <p className="text-[13px] text-[#666] font-sans" data-id="element-134">
                                Servono più elementi.
                              </p>}
                          </div>
                        </motion.section>
                      </div>
                    </div>

                    {/* Sales Correlation Section - Distinct at the bottom */}
                    <motion.section variants={itemVariants} className="mt-20 pt-16 border-t-2 border-black" data-id="element-135">
                      <div className="mb-12" data-id="element-136">
                        <div className="flex items-center justify-between mb-6" data-id="element-137">
                          <h3 className="font-heading font-bold text-[16px] tracking-[0.25em] uppercase text-black flex items-center gap-3" data-id="element-138">
                            <TrendingUp className="w-5 h-5" data-id="element-139" />
                            Allineamento Commerciale
                          </h3>
                          <span className="text-[10px] font-heading tracking-[0.1em] uppercase text-[#999] bg-white px-3 py-1.5 border border-border rounded-full" data-id="element-140">
                            Dati Q4 2024 vs FW25
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-141">
                          <div className="md:col-span-2" data-id="element-142">
                            <p className="text-[14px] text-[#666] font-sans leading-relaxed" data-id="element-143">
                              Analisi predittiva che confronta la direzione
                              creativa attuale del moodboard con i dati storici
                              di vendita e i trend di mercato previsti per la
                              stagione. L'allineamento complessivo è del{' '}
                              <strong className="text-black font-medium" data-id="element-144">
                                72%
                              </strong>
                              , indicando un buon potenziale commerciale ma con
                              alcune discrepanze sui materiali leggeri.
                            </p>
                          </div>
                          <div className="flex items-center md:justify-end gap-6 text-[11px] font-sans text-[#666]" data-id="element-145">
                            <div className="flex items-center gap-2" data-id="element-146">
                              <div className="w-3 h-3 bg-[#E5E5E5]" data-id="element-147" />
                              <span data-id="element-148">Moodboard / Produzione</span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-149">
                              <div className="w-3 h-3 bg-black" data-id="element-150" />
                              <span data-id="element-151">Vendite Storiche</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-152">
                        {/* Category Chart */}
                        <div className="bg-white border border-border p-6 space-y-6 rounded-xl" data-id="element-153">
                          <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-154">
                            Categorie Prodotto
                          </h4>
                          <div className="h-[200px] w-full" data-id="element-155">
                            <ResponsiveContainer width="100%" height="100%" data-id="element-156">
                              <BarChart data={mockSalesData} margin={{
                          top: 10,
                          right: 10,
                          bottom: 20,
                          left: -20
                        }} data-id="element-157">
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F5F5F5" data-id="element-158" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                            fontSize: 10,
                            fill: '#666',
                            fontFamily: 'monospace'
                          }} dy={10} data-id="element-159" />
                                <YAxis axisLine={false} tickLine={false} tick={{
                            fontSize: 10,
                            fill: '#999'
                          }} data-id="element-160" />
                                <Tooltip cursor={{
                            fill: '#F5F5F5'
                          }} contentStyle={{
                            borderRadius: 0,
                            border: '1px solid #E5E5E5',
                            fontSize: '12px',
                            fontFamily: 'sans-serif'
                          }} data-id="element-161" />
                                <Bar dataKey="produzione" name="Moodboard (%)" fill="#E5E5E5" radius={[2, 2, 0, 0]} data-id="element-162" />
                                <Bar dataKey="vendite" name="Vendite (%)" fill="#000000" radius={[2, 2, 0, 0]} data-id="element-163" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Colors Chart */}
                        <div className="bg-white border border-border p-6 space-y-6 rounded-xl" data-id="element-164">
                          <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-165">
                            Performance Colori
                          </h4>
                          <div className="h-[200px] w-full" data-id="element-166">
                            <ResponsiveContainer width="100%" height="100%" data-id="element-167">
                              <BarChart data={mockColorSalesData} margin={{
                          top: 10,
                          right: 10,
                          bottom: 20,
                          left: -20
                        }} data-id="element-168">
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F5F5F5" data-id="element-169" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                            fontSize: 10,
                            fill: '#666',
                            fontFamily: 'monospace'
                          }} dy={10} data-id="element-170" />
                                <YAxis axisLine={false} tickLine={false} tick={{
                            fontSize: 10,
                            fill: '#999'
                          }} data-id="element-171" />
                                <Tooltip cursor={{
                            fill: '#F5F5F5'
                          }} contentStyle={{
                            borderRadius: 0,
                            border: '1px solid #E5E5E5',
                            fontSize: '12px',
                            fontFamily: 'sans-serif'
                          }} data-id="element-172" />
                                <Bar dataKey="moodboard" name="Moodboard (%)" fill="#E5E5E5" radius={[2, 2, 0, 0]} data-id="element-173" />
                                <Bar dataKey="vendite" name="Vendite (%)" fill="#000000" radius={[2, 2, 0, 0]} data-id="element-174" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Fabrics Chart */}
                        <div className="bg-white border border-border p-6 space-y-6 rounded-xl" data-id="element-175">
                          <h4 className="font-heading font-normal text-[11px] tracking-[0.25em] uppercase text-[#666]" data-id="element-176">
                            Adozione Tessuti
                          </h4>
                          <div className="h-[200px] w-full" data-id="element-177">
                            <ResponsiveContainer width="100%" height="100%" data-id="element-178">
                              <BarChart data={mockFabricSalesData} margin={{
                          top: 10,
                          right: 10,
                          bottom: 20,
                          left: -20
                        }} data-id="element-179">
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F5F5F5" data-id="element-180" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                            fontSize: 10,
                            fill: '#666',
                            fontFamily: 'monospace'
                          }} dy={10} data-id="element-181" />
                                <YAxis axisLine={false} tickLine={false} tick={{
                            fontSize: 10,
                            fill: '#999'
                          }} data-id="element-182" />
                                <Tooltip cursor={{
                            fill: '#F5F5F5'
                          }} contentStyle={{
                            borderRadius: 0,
                            border: '1px solid #E5E5E5',
                            fontSize: '12px',
                            fontFamily: 'sans-serif'
                          }} data-id="element-183" />
                                <Bar dataKey="moodboard" name="Moodboard (%)" fill="#E5E5E5" radius={[2, 2, 0, 0]} data-id="element-184" />
                                <Bar dataKey="vendite" name="Vendite (%)" fill="#000000" radius={[2, 2, 0, 0]} data-id="element-185" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    </motion.section>
                  </div>
                </motion.div> : <motion.div key="chat" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="absolute inset-0 overflow-y-auto pb-32 flex flex-col" data-id="element-186">
                  <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-12 flex flex-col justify-end min-h-full" data-id="element-187">
                    <div className="space-y-8" data-id="element-188">
                      {messages.map((msg, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-189">
                          <div className={`max-w-[80%] p-6 rounded-2xl ${msg.role === 'user' ? 'bg-black text-white rounded-br-sm' : 'bg-white border border-border text-black rounded-bl-sm'}`} data-id="element-190">
                            {msg.role === 'ai' && <div className="flex items-center gap-2 mb-3 text-[#666]" data-id="element-191">
                                <Sparkles className="w-4 h-4" data-id="element-192" />
                                <span className="font-heading text-[10px] tracking-[0.2em] uppercase" data-id="element-193">
                                  IA
                                </span>
                              </div>}
                            <p className="text-[15px] font-sans leading-[1.6]" data-id="element-194">
                              {msg.content}
                            </p>
                          </div>
                        </motion.div>)}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Chat Input Bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-border p-6 z-20" data-id="element-195">
            <div className="max-w-3xl mx-auto" data-id="element-196">
              <form onSubmit={handleChatSubmit} className="relative" data-id="element-197">
                <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Chiedi all'IA un'analisi specifica sulla direzione creativa..." className="w-full h-14 pl-6 pr-14 bg-[#F5F5F5] border border-transparent focus:border-black focus:bg-white text-[14px] font-sans outline-none transition-colors placeholder:text-[#999] rounded-full" data-id="element-198" />
                <button type="submit" disabled={!chatInput.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-black hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-black rounded-full" data-id="element-199">
                  <Send className="w-5 h-5" strokeWidth={1.5} data-id="element-200" />
                </button>
              </form>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}